import {
  CommonTrackingData,
  getCommonTrackingData,
  trackingModelIdSplitter,
  VwTrack,
  VwTrackingEvents,
} from '@volkswagen-onehub/vw-e-mobility-feature-app-commons';
import {
  TrackingEvent,
  TrackingFunctions,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {ChargeTimeSimulatorTrackingCtx} from '@volkswagen-onehub/whitelabel-e-mobility-chargetime-feature-app';

const getCommonData = ({
  tenantConfiguration,
  selectedModel,
  selectedTrim,
}: ChargeTimeSimulatorTrackingCtx): CommonTrackingData => {
  const [trackingModelId, trackingModelTrimFeatures] = trackingModelIdSplitter(
    selectedModel.id
  );

  return getCommonTrackingData(!!tenantConfiguration.global?.commercial, {
    EquipmentlineName: selectedTrim.trimId,
    CarlineId: selectedModel.carlineId,
    CarlineName: selectedModel.carlineName,
    SalesgroupId: selectedModel.salesgroupId,
    SalesgroupName: selectedModel.salesgroupName,
    ModelId: trackingModelId,
    ModelTrimFeatures: trackingModelTrimFeatures,
    ModelName: selectedModel.name,
    EnginePowerHPKW: selectedModel.engine,
  });
};

export function getChargeTimeTrackingFunctions(
  track: VwTrack
): TrackingFunctions<ChargeTimeSimulatorTrackingCtx> {
  return {
    [TrackingEvent.FeatureAppLoaded]: (_, ctx) =>
      track(
        VwTrackingEvents.VWBasic_eMobilityTools_Load,
        getCommonData(ctx),
        undefined
      ),
    [TrackingEvent.EducationalInfoClick]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_VehicleLink_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.ModelEfficiencyDataLayerOpen]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_VehicleLink_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.ChargingLocationChange]: ({location}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [location],
          name: 'charging-location-select',
        },
      }),
    [TrackingEvent.ChargerTypeChange]: ({chargerType}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [chargerType],
          name: 'charger-type-select',
        },
      }),
    [TrackingEvent.ChargingPowerChange]: ({chargingPower}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [chargingPower],
          name: 'charging-power-select',
        },
      }),
    [TrackingEvent.ChargeDeltaChange]: ({percentages}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'range',
          lowerbound: percentages[0],
          upperbound: percentages[1],
          name: 'charging-range',
        },
      }),
    [TrackingEvent.OutsideTemperatureChange]: ({temperatureCelsius}, ctx) =>
      track(VwTrackingEvents.VWBasic_FilterSelect_Click, getCommonData(ctx), {
        filter: {
          type: 'single_value',
          values: [temperatureCelsius],
          name: 'outside-temperature',
        },
      }),
    [TrackingEvent.ElliWallboxExternalLinkClick]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_Link_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.BasicButtonClick]: ({label, link}, ctx) =>
      track(VwTrackingEvents.VWBasic_Button_Click, getCommonData(ctx), {
        eventInfo: {
          contentId: label,
        },
        link,
      }),
    [TrackingEvent.ChargeTimeTrimSelect]: ({trim, model}, ctx) => {
      const [trackingModelId, trackingModelTrimFeatures] =
        trackingModelIdSplitter(model.id);
      track(VwTrackingEvents.VWBasic_VehicleSelect_Click, getCommonData(ctx), {
        configuration: {
          // We need to overwrite all this from the common data with the new
          // values.
          EquipmentlineName: trim.trimId,
          CarlineId: model.carlineId,
          CarlineName: model.carlineName,
          SalesgroupId: model.salesgroupId,
          SalesgroupName: model.salesgroupName,
          ModelId: trackingModelId,
          ModelTrimFeatures: trackingModelTrimFeatures,
          ModelName: model.name,
          EnginePowerHPKW: model.engine,
        },
      });
    },
    [TrackingEvent.ChargeTimeEngineSelect]: ({model}, ctx) => {
      const [trackingModelId, trackingModelTrimFeatures] =
        trackingModelIdSplitter(model.id);

      track(VwTrackingEvents.VWBasic_EngineSelect_Click, getCommonData(ctx), {
        configuration: {
          // We need to overwrite all this from the common (old) data with the new
          // values.
          ModelId: trackingModelId,
          ModelTrimFeatures: trackingModelTrimFeatures,
          ModelName: model.name,
          EnginePowerHPKW: model.engine,
        },
      });
    },
  };
}
